import axios from "axios";
import { logger } from "@/utils/logger";
import { getAppStore } from "@/stores/app/app.bridge";
import { getFleetStore } from "@/stores/fleet/fleet.getters.bridge";
import { applyAnalysisData, applySeferPlani, savePlanEntry, } from "./plan-manager.sync";
/** Pinia plan-manager async actions — Vuex planManager.js actions (micro-7-2) */
export const planManagerAsyncActions = {
    async selectDirectory() {
        try {
            const handle = await window.showDirectoryPicker({
                mode: "readwrite",
                startIn: "documents",
            });
            this.directoryHandle = handle;
            return true;
        }
        catch (error) {
            if (error.name === "AbortError") {
                return false;
            }
            logger.error("Klasör seçme hatası:", error?.message || error);
            return false;
        }
    },
    async savePlan({ hatNo, gunTipi, plan }) {
        try {
            if (!this.directoryHandle) {
                logger.error("Klasör seçili değil");
                return false;
            }
            const hatFolder = await this.directoryHandle.getDirectoryHandle(`hat_${hatNo}`, { create: true });
            const fileHandle = await hatFolder.getFileHandle(`${gunTipi}.json`, {
                create: true,
            });
            const writable = await fileHandle.createWritable();
            await writable.write(JSON.stringify(plan, null, 2));
            await writable.close();
            savePlanEntry(this, { hatNo, gunTipi, plan });
            return true;
        }
        catch (error) {
            logger.error("Plan kaydetme hatası:", error?.message || error);
            return false;
        }
    },
    async loadPlan({ hatNo, gunTipi }) {
        logger.debug("loadPlan action çağrıldı:", { hatNo, gunTipi });
        try {
            if (!this.directoryHandle) {
                logger.error("Çalışma klasörü seçili değil");
                return false;
            }
            try {
                logger.debug("Hat klasörü açılıyor:", `hat_${hatNo}`);
                const hatFolder = await this.directoryHandle.getDirectoryHandle(`hat_${hatNo}`);
                logger.debug("Plan dosyası okunuyor:", `${gunTipi}.json`);
                const fileHandle = await hatFolder.getFileHandle(`${gunTipi}.json`);
                const file = await fileHandle.getFile();
                const planData = JSON.parse(await file.text());
                logger.debug("Okunan plan verisi:", planData);
                if (planData.analysisData) {
                    logger.debug("Analiz verileri yükleniyor:", planData.analysisData);
                    applyAnalysisData(this, planData.analysisData);
                }
                if (planData.sefer_plani) {
                    logger.debug("Sefer planı yükleniyor:", planData.sefer_plani);
                    applySeferPlani(this, planData.sefer_plani);
                }
                if (planData.arac_plani?.araclar) {
                    const vehicles = planData.arac_plani.araclar;
                    logger.debug("Araç planı güncelleniyor:", vehicles);
                    getFleetStore().setVehicles(vehicles);
                }
                if (planData.currentMethod) {
                    this.currentMethod = planData.currentMethod;
                }
                if (planData.dolulukHedefi) {
                    this.dolulukHedefi = planData.dolulukHedefi;
                }
                if (planData.optimizasyonParametreleri) {
                    this.optimizasyonParametreleri = planData.optimizasyonParametreleri;
                }
                if (planData.analizSonuclari) {
                    this.analizSonuclari = planData.analizSonuclari;
                }
                logger.debug("Plan başarıyla yüklendi");
                return true;
            }
            catch (error) {
                logger.error("Plan dosyası okuma hatası:", error);
                return false;
            }
        }
        catch (error) {
            logger.error("Plan yükleme hatası:", error?.message || error);
            return false;
        }
    },
    async listPlans() {
        try {
            if (!this.directoryHandle) {
                throw new Error("Önce çalışma klasörü seçmelisiniz");
            }
            const planlar = [];
            for await (const hatEntry of this.directoryHandle.values()) {
                if (hatEntry.kind === "directory" && hatEntry.name.startsWith("hat_")) {
                    const hatNo = hatEntry.name.replace("hat_", "");
                    const hatFolder = await this.directoryHandle.getDirectoryHandle(hatEntry.name);
                    const gunler = [];
                    for await (const fileEntry of hatFolder.values()) {
                        if (fileEntry.kind === "file" && fileEntry.name.endsWith(".json")) {
                            gunler.push(fileEntry.name.replace(".json", ""));
                        }
                    }
                    planlar.push({ hatNo, gunler });
                }
            }
            return planlar;
        }
        catch (error) {
            logger.error("Plan listesi alma hatası:", error);
            return [];
        }
    },
    async exportToDatabase({ hatNo, gunTipi, connection }) {
        try {
            const hatFolder = await this.directoryHandle.getDirectoryHandle(`hat_${hatNo}`);
            const fileHandle = await hatFolder.getFileHandle(`${gunTipi}.json`);
            const file = await fileHandle.getFile();
            const plan = JSON.parse(await file.text());
            const response = await axios.post("/api/export-to-database", {
                hatNo,
                gunTipi,
                plan,
                connection,
            });
            return response.data.success;
        }
        catch (error) {
            logger.error("Veritabanına aktarma hatası:", error);
            return false;
        }
    },
    async loadPlanFromDatabase({ planId, connection }) {
        try {
            const response = await axios.get("/get-plan-detail", {
                params: {
                    plan_id: planId,
                    connection: connection,
                },
            });
            if (!response.data.success) {
                throw new Error(response.data.error || "Plan yüklenemedi");
            }
            const planData = response.data.plan;
            if (planData.analysisData) {
                applyAnalysisData(this, planData.analysisData);
            }
            else {
                throw new Error("Plan verisinde analiz bilgileri eksik");
            }
            if (planData.currentMethod) {
                this.currentMethod = planData.currentMethod;
                getAppStore().setCurrentMethod(planData.currentMethod);
            }
            if (planData.dolulukHedefi) {
                this.dolulukHedefi = planData.dolulukHedefi;
            }
            if (planData.optimizasyonParametreleri) {
                this.optimizasyonParametreleri = planData.optimizasyonParametreleri;
            }
            if (planData.seferPlani) {
                applySeferPlani(this, planData.seferPlani);
            }
            if (planData.currentMethod === 4 && planData.selectedBeta) {
                getAppStore().setBeta(planData.selectedBeta);
            }
            return true;
        }
        catch (error) {
            logger.error("Veritabanından plan yükleme hatası:", error);
            throw error;
        }
    },
};
